import { useCallback } from 'react'

import { ScenarioPlan } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getBlockerDataDetail } from '@cash-web/shared-flow-data-access-context'
import { CashResponse, CashResponseContext, CashResponseMiddleware } from '@cash-web/shared-util-fetch'
import { BlockerFlowInteractReceiveJourneyOutcome } from '@squareup/customer-data-events-web'

import { FlowState } from '../../hooks/flowReducer'

/**
 * Hook that creates a middleware that logs CDF events when journey outcomes are received in flow responses.
 * This logs BlockerFlowInteractReceiveJourneyOutcome events for analytics tracking.
 */
export const useCreateCdfJourneyOutcomeResponseMiddleware = ({
  state,
}: {
  state: FlowState
}): CashResponseMiddleware => {
  const logCdfEvent = useLogCashWebCdfEvent()

  return useCallback(
    (response: CashResponse<{ scenario_plan?: ScenarioPlan }>, context?: CashResponseContext) => {
      // Only log if there's a journey outcome in the response
      if (response?.scenario_plan?.journey_outcome) {
        // Get flow information directly from state instead of parsing headers
        const blockerData = state.activeBlockerDescriptor
          ? getBlockerDataDetail(state.activeBlockerDescriptor)
          : undefined

        logCdfEvent(
          BlockerFlowInteractReceiveJourneyOutcome({
            clientScenario: state.clientScenario,
            flowToken: state.flowToken,
            originatingFlowType: state.originatingFlowType,
            lastBlockerId: blockerData?.id,
            lastBlockerType: blockerData?.type,
            outcome: response.scenario_plan.journey_outcome,
          })
        )
      }

      return response
    },
    [logCdfEvent, state.activeBlockerDescriptor, state.clientScenario, state.flowToken, state.originatingFlowType]
  )
}
